import React, { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavBar from './NavBar';
import { supportedLanguages, SupportedLanguage } from '../i18n';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'de';
  const location = useLocation();

  const switchLanguage = (newLang: SupportedLanguage) => {
    const pathWithoutLang = location.pathname.replace(/^\/(de|fr|en)/, '');
    window.location.href = `/${newLang}${pathWithoutLang || ''}`;
  };

  useEffect(() => {
    const onScroll = () => {
      document.body.classList.toggle('has-scrolled', window.scrollY > 60);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Every new route starts at the top (unless the URL includes a hash/anchor — the page handles those itself).
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [location.pathname, location.hash]);

  // Auto-scroll away from the hero after 5s on pages that have one, unless the visitor
  // already took over (scrolled, tapped, typed) or asked for reduced motion.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let cancelled = false;
    let rafId: number | null = null;

    const userEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];

    const cleanup = () => {
      cancelled = true;
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      window.clearTimeout(timer);
      userEvents.forEach((event) => window.removeEventListener(event, cleanup));
    };

    const autoScroll = () => {
      if (cancelled) return;
      if (!document.querySelector('.hero-section')) return;
      if (window.scrollY > 10) return;

      const target = window.innerHeight * 0.35;
      const startY = window.scrollY;
      const distance = target - startY;
      const duration = 1200; // ms — slower than the browser's smooth scroll default
      const startTime = window.performance.now();

      const step = (now: number) => {
        if (cancelled) return;
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2; // ease-in-out-cubic
        window.scrollTo({ top: startY + distance * ease, behavior: 'auto' });
        if (progress < 1) rafId = window.requestAnimationFrame(step);
      };

      rafId = window.requestAnimationFrame(step);
    };

    const timer = window.setTimeout(autoScroll, 5000);

    userEvents.forEach((event) => window.addEventListener(event, cleanup, { passive: true }));

    return cleanup;
  }, [location.pathname]);

  const isAppointmentPage = location.pathname.replace(/^\/(de|fr|en)/, '') === '/appointment';

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1 pt-0">{children}</main>
      {!isAppointmentPage && (
        <Link
          to={`/${currentLang}/appointment`}
          className="fixed bottom-6 right-6 z-50 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-5 py-3 rounded-full shadow-lg transition"
        >
          {t('nav.appointment')}
        </Link>
      )}
      <footer className="bg-primary-950 text-gray-400 py-8 px-4">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <div className="flex gap-6">
            <a href={`/${currentLang}/impressum`} className="hover:text-white transition">
              {t('footer.impressum')}
            </a>
            <a href={`/${currentLang}/contact`} className="hover:text-white transition">
              {t('contact.title')}
            </a>
          </div>
          <div className="language-switcher">
            {supportedLanguages.map((lng) => (
              <button
                key={lng}
                onClick={() => switchLanguage(lng)}
                className={`lang-btn ${currentLang === lng ? 'active' : ''}`}
                aria-label={t(`language.${lng}`)}
              >
                {lng.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
